// Mock backend for dev (VITE_USE_MOCK_API=true). Drives the UX end-to-end
// without a live server. Simulates an in-progress provisioning job that
// ticks through the 11 orchestrator steps every ~3s for a snappy demo.

import {
  ConflictError,
  type CreatePlaygroundRequest,
  type CreatePlaygroundResponse,
  type DnsZone,
  type Me,
  type Playground,
  type PlaygroundStatus,
  type PlaygroundStatusResponse,
  type SharedoVersionsResponse,
} from "./types";

const ME: Me = {
  upn: "alice@sharedo.com",
  oid: "00000000-0000-0000-0000-000000000001",
  roles: ["Playgrounds.User", "Playgrounds.Admin"],
  runningJobs: [],
};

const TOTAL_STEPS = 11;
const STEP_LABELS = [
  "Synth CDKTF",
  "Apply shared infra",
  "Provision customer infra",
  "Provision customer app",
  "Deploy Operate bits",
  "Run DB migrations",
  "Configure Event Engine",
  "Wire OIDC",
  "Bind custom hostname",
  "Warm up app",
  "Smoke test",
];

interface MockState {
  customer: string;
  dnsZone: string;
  fqdn: string;
  status: PlaygroundStatus;
  createdBy: { oid: string; upn: string };
  createdAt: string;
  step: number;
  log: string[];
  jobId?: string;
  sharedoVersion: string;
  eeVersion: string;
  ring: string;
  startedAt: number;
}

const store = new Map<string, MockState>();

// Seed: a healthy one, an in-flight one, a failed one, and an old destroying one.
seed("acme", "ready", 11);
seed("zenith", "provisioning", 7);
seed("legacy-co", "failed", 7);
seed("delta", "ready", 11);
seed("echo", "destroying", 0);

function seed(customer: string, status: PlaygroundStatus, step: number) {
  const age = {
    acme: 3 * 24 * 3600 * 1000,
    zenith: 8 * 60 * 1000,
    "legacy-co": 60 * 60 * 1000,
    delta: 12 * 24 * 3600 * 1000,
    echo: 30 * 1000,
  }[customer] ?? 60_000;
  const owner =
    customer === "zenith"
      ? { oid: "bob", upn: "bob@sharedo.com" }
      : customer === "legacy-co"
        ? { oid: "carol", upn: "carol@sharedo.com" }
        : ME;
  store.set(customer, {
    customer,
    dnsZone: "test.sharedo.cloud",
    fqdn: `${customer}.test.sharedo.cloud`,
    status,
    createdBy: { oid: owner.oid, upn: owner.upn },
    createdAt: new Date(Date.now() - age).toISOString(),
    step,
    log: seedLog(customer, status, step),
    jobId: status === "provisioning" || status === "destroying" ? `job-${customer}` : undefined,
    sharedoVersion: "7.11.4",
    eeVersion: "pinned",
    ring: "default",
    startedAt: Date.now() - age,
  });
}

function seedLog(customer: string, status: PlaygroundStatus, step: number): string[] {
  const lines: string[] = [];
  const start = new Date(Date.now() - 6 * 60 * 1000);
  for (let i = 0; i < Math.min(step, TOTAL_STEPS); i++) {
    const t = new Date(start.getTime() + i * 35_000).toISOString().substring(11, 19);
    lines.push(`${t}  [step ${i + 1}/${TOTAL_STEPS}] ${STEP_LABELS[i]} — starting`);
    lines.push(`${t}  [step ${i + 1}/${TOTAL_STEPS}] ${STEP_LABELS[i]} — ok`);
  }
  if (status === "provisioning" && step < TOTAL_STEPS) {
    lines.push(`         [step ${step + 1}/${TOTAL_STEPS}] ${STEP_LABELS[step]} — running`);
  }
  if (status === "failed") {
    lines.push(`         [step ${step}/${TOTAL_STEPS}] ${STEP_LABELS[step - 1]} — FAILED`);
    lines.push(
      `         Error: EE health check returned 503 after 30 retries.`,
    );
  }
  if (status === "destroying") {
    lines.push(`         destroy job started for ${customer}`);
  }
  return lines;
}

function getMe(): Me {
  const running = [...store.values()]
    .filter(
      (p) =>
        (p.status === "provisioning" || p.status === "destroying") &&
        p.createdBy.upn === ME.upn,
    )
    .map((p) => ({
      customer: p.customer,
      status: p.status as "provisioning" | "destroying",
      step: p.step,
      totalSteps: TOTAL_STEPS,
      jobId: p.jobId ?? `job-${p.customer}`,
    }));
  return { ...ME, runningJobs: running };
}

function toPlayground(s: MockState): Playground {
  return {
    customer: s.customer,
    dnsZone: s.dnsZone,
    fqdn: s.fqdn,
    status: s.status,
    createdBy: s.createdBy,
    createdAt: s.createdAt,
    sharedoVersion: s.sharedoVersion,
    eeVersion: s.eeVersion,
    ring: s.ring,
    step: s.step,
    totalSteps: TOTAL_STEPS,
    lastJob: s.jobId
      ? { id: s.jobId, kind: s.status === "destroying" ? "destroy" : "deploy", startedAt: s.createdAt }
      : undefined,
  };
}

// Advance any active jobs each tick.
setInterval(() => {
  for (const s of store.values()) {
    if (s.status === "provisioning" && s.step < TOTAL_STEPS) {
      s.step += 1;
      const t = new Date().toISOString().substring(11, 19);
      s.log.push(`${t}  [step ${s.step}/${TOTAL_STEPS}] ${STEP_LABELS[s.step - 1]} — ok`);
      if (s.step >= TOTAL_STEPS) {
        s.status = "ready";
        s.log.push(`${t}  deploy complete in ${(Date.now() - s.startedAt) / 1000 | 0}s`);
      }
    } else if (s.status === "destroying") {
      const t = new Date().toISOString().substring(11, 19);
      s.log.push(`${t}  destroy progressing for ${s.customer}`);
      if (Date.now() - s.startedAt > 15_000) {
        store.delete(s.customer);
      }
    }
  }
}, 3000);

// API surface --------------------------------------------------------------

export async function me(): Promise<Me> {
  return getMe();
}

export async function listPlaygrounds(): Promise<Playground[]> {
  return [...store.values()].map(toPlayground);
}

export async function getPlayground(customer: string): Promise<Playground> {
  const s = store.get(customer);
  if (!s) throw new Error("not found");
  return toPlayground(s);
}

export async function getStatus(
  customer: string,
  since?: number,
): Promise<PlaygroundStatusResponse> {
  const s = store.get(customer);
  if (!s) throw new Error("not found");
  const cursor = since ?? 0;
  const recentLog = s.log.slice(cursor);
  return {
    status: s.status,
    jobId: s.jobId,
    step: s.step,
    totalSteps: TOTAL_STEPS,
    completedSteps: Array.from({ length: s.step }, (_, i) => i + 1),
    recentLog,
    logCursor: s.log.length,
    failureSummary:
      s.status === "failed"
        ? `Step ${s.step} failed: ${STEP_LABELS[s.step - 1]} — EE health check returned 503 after 30 retries.`
        : undefined,
  };
}

export async function getFullLog(customer: string): Promise<string> {
  const s = store.get(customer);
  if (!s) throw new Error("not found");
  return s.log.join("\n");
}

export async function create(req: CreatePlaygroundRequest): Promise<CreatePlaygroundResponse> {
  if (store.has(req.customer)) {
    throw new ConflictError(`An \`${req.customer}\` playground already exists or is being deployed.`);
  }
  const jobId = `job-${req.customer}-${Date.now()}`;
  store.set(req.customer, {
    customer: req.customer,
    dnsZone: req.dnsZone,
    fqdn: `${req.customer}.${req.dnsZone}`,
    status: "provisioning",
    createdBy: { oid: ME.oid, upn: ME.upn },
    createdAt: new Date().toISOString(),
    step: 1,
    log: [
      `${new Date().toISOString().substring(11, 19)}  [step 1/${TOTAL_STEPS}] ${STEP_LABELS[0]} — starting`,
    ],
    jobId,
    sharedoVersion: req.sharedoVersion ?? "7.11.4",
    eeVersion: req.eeVersion ?? "pinned",
    ring: req.ring ?? "default",
    startedAt: Date.now(),
  });
  return { jobId, customer: req.customer };
}

export async function destroy(customer: string): Promise<void> {
  const s = store.get(customer);
  if (!s) return;
  s.status = "destroying";
  s.jobId = `job-destroy-${customer}-${Date.now()}`;
  s.startedAt = Date.now();
  s.log.push(`${new Date().toISOString().substring(11, 19)}  destroy job started for ${customer}`);
}

export async function listDnsZones(): Promise<DnsZone[]> {
  return [
    { zone: "test.sharedo.cloud", default: true },
    { zone: "playground.sharedo.cloud" },
  ];
}

export async function listSharedoVersions(
  ring: "staging" | "playground",
): Promise<SharedoVersionsResponse> {
  if (ring === "staging") {
    return {
      versions: [
        { version: "7.11.4", publishedAt: "2026-05-10T14:00:00Z" },
        { version: "7.11.3", publishedAt: "2026-04-22T11:30:00Z" },
        { version: "7.11.2", publishedAt: "2026-04-08T09:15:00Z" },
        { version: "7.11.1", publishedAt: "2026-03-19T16:42:00Z" },
        { version: "7.11.0", publishedAt: "2026-03-04T10:00:00Z" },
      ],
    };
  }
  return {
    versions: [
      { version: "hackathon-q1-document-ai", publishedAt: "2026-05-20T15:20:00Z" },
      { version: "alice-feature-bulk-import", publishedAt: "2026-05-12T11:00:00Z" },
      { version: "mvp-realtime-dashboard", publishedAt: "2026-04-30T09:30:00Z" },
    ],
  };
}
